import React, { useState, useEffect } from "react";
import { cn } from "../../../lib/utils";
import { ReactComponent as Pen } from "../../../assets/icons/pen.svg";
import { ReactComponent as Undo } from "../../../assets/icons/undo.svg";
import APIVariables from "../../../services/api-variables";
import {
  handleCatch,
  isEmptyObject,
  prescriberName,
} from "../../../shared/helpers";
import LocalStorage from "../../../shared/localStorage";
import Loader from "../../common/Loader/Loader";
import useRoles from "../../../hooks/useRoles";
import Axios from "../../../services/Axios";
import Wholesaler from "../../common/Wholesaler/Wholesaler";
import { errorMessages } from "../../../constants/errorMessages.constants";
import ToggleSwitch from "../../common/ToggleSwitch/ToggleSwitch";
import { DrugRegistration } from "./DrugRegistration/DrugRegistration";

/** Shared underline field style (matches legacy profile form). */
const fieldInput =
  "mt-2.5 mb-4 w-full border-0 border-b border-neutral-300 bg-[#f8f8f8] px-0 py-1 text-lg outline-none disabled:cursor-not-allowed";

const Profile = ({ setLoggedInInitials }) => {
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    isPrescriber,
    isPharmacy,
    isConnectedNurse,
    isHomecare,
    isOutpatient,
  } = useRoles();

  const [profileDetails, setProfileDetails] = useState({});
  const [unchangeProfileDetails, setUnchangeProfileDetails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [isLoadingSaveButton, setIsLoadingSaveButton] = useState(false);

  const invalidWholeSaler = (errorMessage || []).find((element) =>
    element.includes("wholesalers_data")
  );
  const getProfileData = async () => {
    await Axios.get(APIVariables.USERS_PROFILE)
      .then((res) => {
        const { status, data } = res;
        if (status === 200) {
          setProfileDetails(data);
          setUnchangeProfileDetails(data);
        }
      })
      .catch((error) => {
        handleCatch(error);
      });
    setIsLoading(false);
  };

  useEffect(() => {
    getProfileData();

    // When destroy profile component, remove IS_EDIT_MODE_NHSPORTAL  from localstorage
    return () => {
      localStorage.removeItem(LocalStorage.IS_EDIT_MODE_NHSPORTAL);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "delivery_address" || name === "ordering_address") {
      const copyProfileDetails = {
        ...profileDetails,
        pharmacy: { ...profileDetails.pharmacy, [name]: value },
      };
      setProfileDetails(copyProfileDetails);
    } else {
      setProfileDetails((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const removeWhiteSpaces = (element) => {
    return element.replace(/\s{2,}/g, " ").trim();
  };

  const handleChangeWholeSaler = (e) => {
    const { name, value } = e.target;

    const amendCurrentWholeSaler = profileDetails.wholesalers_data.map(
      (element) => {
        if (element.id === +name) {
          return { ...element, account_number: value };
        } else {
          return element;
        }
      }
    );

    const copyProfileDetails = {
      ...profileDetails,
      wholesalers_data: amendCurrentWholeSaler,
    };
    setProfileDetails(copyProfileDetails);
  };

  const handleSaveButtonClick = async () => {
    setIsLoadingSaveButton(true);
    await onClickEditSave();
    setIsLoadingSaveButton(false);
  };

  const onClickEditSave = async () => {
    setIsSubmitting(false);
    setErrorMessage("");
    if (!isEditMode) {
      setIsEditMode(true);
      localStorage.setItem(LocalStorage.IS_EDIT_MODE_NHSPORTAL, true);
    }
    if (isEditMode) {
      setIsSubmitting(true);
      localStorage.removeItem(LocalStorage.IS_EDIT_MODE_NHSPORTAL);
      if (areAllFields) {
        const {
          last_name,
          job_title,
          wholesalers_data,
          pharmacy,
          receive_new_paf_emails,
        } = profileDetails;

        const checkObject = {
          ...(unchangeProfileDetails.last_name !== last_name && {
            last_name: removeWhiteSpaces(last_name),
          }),
          ...(unchangeProfileDetails.job_title !== job_title && {
            job_title: removeWhiteSpaces(job_title),
          }),
          ...(unchangeProfileDetails.wholesalers_data !== wholesalers_data && {
            wholesalers_data,
          }),
          ...((isPharmacy || isHomecare || isOutpatient) && {
            ...(unchangeProfileDetails.pharmacy.delivery_address !==
              pharmacy.delivery_address && {
              wholesalers_data,
            }),
            ...(unchangeProfileDetails.pharmacy.ordering_address !==
              pharmacy.ordering_address && {
              wholesalers_data,
            }),
            ...(unchangeProfileDetails.receive_new_paf_emails !==
              receive_new_paf_emails && {
              receive_new_paf_emails,
            }),
          }),
        };

        if (!isEmptyObject(checkObject)) {
          let res;

          const object = {
            last_name,
            job_title,
            wholesalers_data,
            ...((isPharmacy || isHomecare || isOutpatient) && {
              delivery_address: pharmacy.delivery_address,
              ordering_address: pharmacy.ordering_address,
              receive_new_paf_emails: receive_new_paf_emails,
            }),
          };

          res = await Axios.put(APIVariables.USERS_PROFILE_EDIT, object).catch(
            function (error) {
              handleCatch(error);

              const errors = error.response.data;
              const errorsArray = [];

              if (typeof errors === "string") {
                errorsArray.push(errorMessages.INTERNAL_SERVER_ERROR);
              } else {
                Object.keys(errors).forEach((key) => {
                  errorsArray.push(key + " - " + errors[key]);
                });
              }
              setErrorMessage(errorsArray);
            }
          );

          if (res?.status === 200) {
            const newProfileDetails = {
              ...profileDetails,
              last_name: removeWhiteSpaces(last_name),
              job_title: removeWhiteSpaces(job_title),
              wholesalers_data,
            };

            updateLocalStorage(newProfileDetails);

            setLoggedInInitials(
              `${newProfileDetails.first_name?.charAt(
                0
              )}${newProfileDetails.last_name?.charAt(0)}`
            );

            setUnchangeProfileDetails(newProfileDetails);
            setProfileDetails(newProfileDetails);

            setIsEditMode(false);
          }
        } else {
          setIsEditMode(false);
        }
      }
    }
  };

  const updateLocalStorage = () => {
    const storage = localStorage.getItem(LocalStorage.USER_NHSPORTAL);
    let parseStorage = JSON.parse(storage);

    parseStorage = {
      ...parseStorage,
      last_name: profileDetails.last_name,
      job_title: profileDetails.job_title,
    };

    localStorage.setItem(
      LocalStorage.USER_NHSPORTAL,
      JSON.stringify(parseStorage)
    );
  };

  const atLeastOneWhoSalertContainsValue = (wholesalers_data) => {
    return (wholesalers_data || []).some(
      (element) => element.account_number.length
    );
  };

  const validateForm = ({
    last_name = "",
    job_title = "",
    wholesalers_data = "",
  }) => ({
    last_name: {
      isValid: last_name?.trim().length > 0,
    },
    job_title: {
      isValid: job_title?.trim().length > 0,
    },
    wholesalers_data: {
      isValid:
        isPharmacy || isHomecare || isOutpatient
          ? atLeastOneWhoSalertContainsValue(wholesalers_data)
          : true,
    },
  });

  const { last_name, job_title, wholesalers_data } = profileDetails;

  const validateScreenFields = validateForm({
    last_name,
    job_title,
    wholesalers_data,
  });

  const areAllFields = Object.values(validateScreenFields).every((field) => {
    return field.isValid;
  });

  const areChanges = () => {
    return (
      JSON.stringify(unchangeProfileDetails) !== JSON.stringify(profileDetails)
    );
  };

  const setUndoChanges = () => {
    setProfileDetails(unchangeProfileDetails);
    setIsEditMode(false);
  };

  const onChangeCheckEmail = () => {
    const newProfile = {
      ...profileDetails,
      receive_new_paf_emails: !profileDetails.receive_new_paf_emails,
    };
    setProfileDetails(newProfile);
  };

  return (
    <div
      className="min-h-screen w-full overflow-y-auto bg-neutral-100 pt-[140px] pb-[100px]"
      data-testid="profile-page"
    >
      {isLoading ? (
        <Loader />
      ) : (
        <React.Fragment>
          <div
            className="mx-auto mt-4 w-full max-w-[1100px] rounded-lg bg-white px-6 py-5 shadow-xl sm:px-10"
            data-testid="profile-details-card"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1
                className="min-w-0 flex-1 text-xl font-semibold text-neutral-800"
                data-testid="profile-heading"
              >{`${
                isPrescriber || isConnectedNurse
                  ? prescriberName()
                  : profileDetails?.pharmacy?.address
              }`}</h1>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {areChanges() && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 border-0 bg-transparent text-lg text-neutral-800 hover:text-neutral-500"
                    onClick={() => setUndoChanges()}
                    data-testid="profile-undo-button"
                  >
                    <Undo /> Undo Changes
                  </button>
                )}

                <button
                  type="button"
                  disabled={isLoadingSaveButton}
                  className="inline-flex h-[46px] items-center rounded-full border-2 border-[#00567a] bg-[#00567a] px-5 text-lg font-bold text-white disabled:opacity-60 [&_svg]:ml-2.5 [&_svg_path]:stroke-white"
                  onClick={handleSaveButtonClick}
                  data-testid="profile-edit-save-button"
                >
                  {!isEditMode ? "Edit Profile" : "Save Edits"} <Pen />
                </button>
              </div>
            </div>

            <hr className="my-4 h-px border-0 bg-[#768692]" />

            <h2 className="mt-5 text-lg font-semibold text-[#00567a]">
              Personal Details
            </h2>

            <div
              className={cn(
                "my-5 space-y-6",
                !isEditMode && "[&_label]:text-neutral-500"
              )}
            >
              <div className="grid gap-6 md:grid-cols-2">
                <div className="relative">
                  <label
                    className={cn(
                      "block",
                      isEditMode && "text-neutral-600"
                    )}
                    data-testid="profile-first-name-label"
                  >
                    {isPrescriber
                      ? "Prescriber First Name:"
                      : isPharmacy || isHomecare || isOutpatient
                      ? "Pharmacist First Name:"
                      : "Nurse First Name:"}
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={profileDetails.first_name}
                    disabled
                    className={cn(fieldInput, "text-neutral-600")}
                    data-testid="profile-first-name-input"
                  />
                </div>
                <div className="relative">
                  <label
                    className={cn(
                      "block",
                      isEditMode ? "text-[#00567a]" : undefined
                    )}
                    data-testid="profile-last-name-label"
                  >
                    {isPrescriber
                      ? "Prescriber Last Name*"
                      : isPharmacy || isHomecare || isOutpatient
                      ? "Pharmacist Last Name*"
                      : "Nurse Last Name*"}
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={profileDetails.last_name}
                    disabled={!isEditMode}
                    onChange={handleChange}
                    maxLength={30}
                    className={cn(
                      fieldInput,
                      "text-neutral-600",
                      isSubmitting &&
                        !validateScreenFields.last_name.isValid &&
                        "border-b-[#e41818]"
                    )}
                    data-testid="profile-last-name-input"
                  />
                  {isSubmitting && !validateScreenFields.last_name?.isValid && (
                    <span className="absolute bottom-0 left-2.5 text-sm font-light italic text-red-600">
                      Required
                    </span>
                  )}
                </div>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="relative">
                  <label
                    className={cn(
                      "block",
                      isEditMode && "text-neutral-600"
                    )}
                    data-testid="profile-registration-number-label"
                  >
                    {isPrescriber || isConnectedNurse
                      ? "Registration Number:"
                      : "GPhC / PSNI Registration Number:"}
                  </label>
                  <input
                    type="text"
                    name="registration_number"
                    value={profileDetails.registration_number}
                    disabled
                    className={cn(fieldInput, "text-neutral-600")}
                    data-testid="profile-registration-number-input"
                  />
                </div>

                <div className="relative">
                  <label
                    className={cn(
                      "block",
                      isEditMode ? "text-[#00567a]" : undefined
                    )}
                    data-testid="profile-job-title-label"
                  >
                    Job Title*
                  </label>
                  <input
                    type="text"
                    name="job_title"
                    value={profileDetails.job_title}
                    disabled={!isEditMode}
                    onChange={handleChange}
                    maxLength={30}
                    className={cn(
                      fieldInput,
                      "text-neutral-600",
                      isSubmitting &&
                        !validateScreenFields.job_title.isValid &&
                        "border-b-[#e41818]"
                    )}
                    data-testid="profile-job-title-input"
                  />
                  {isSubmitting && !validateScreenFields.job_title?.isValid && (
                    <span className="absolute bottom-0 left-2.5 text-sm font-light italic text-red-600">
                      Required
                    </span>
                  )}
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="relative">
                  <label
                    className={cn(
                      "block",
                      isEditMode && "text-neutral-600"
                    )}
                    data-testid="profile-institution-name-label"
                  >
                    {isPrescriber || isConnectedNurse
                      ? "Hospital Name:"
                      : "Pharmacy Name"}
                  </label>
                  <input
                    type="text"
                    name="hospital_name"
                    value={
                      isPrescriber || isConnectedNurse
                        ? profileDetails?.hospital?.value
                        : profileDetails?.pharmacy?.value
                    }
                    disabled
                    className={cn(fieldInput, "text-neutral-600")}
                    data-testid="profile-institution-name-input"
                  />
                </div>

                <div className="relative">
                  <label
                    className={cn(
                      "block",
                      isEditMode && "text-neutral-600"
                    )}
                    data-testid="profile-institution-address-label"
                  >
                    {isPrescriber || isConnectedNurse
                      ? "Address:"
                      : "Pharmacy Address:"}
                  </label>
                  <input
                    type="text"
                    name="hospital_address"
                    value={
                      isPrescriber || isConnectedNurse
                        ? profileDetails?.hospital?.address
                        : profileDetails?.pharmacy?.address
                    }
                    disabled
                    className={cn(fieldInput, "text-neutral-600")}
                    data-testid="profile-institution-address-input"
                  />
                </div>
              </div>

              {isPharmacy || isHomecare || isOutpatient ? (
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="relative">
                    <label
                      className={cn(
                        "block",
                        isEditMode ? "text-[#00567a]" : undefined
                      )}
                      data-testid="profile-delivery-address-label"
                    >
                      Delivery Address:
                    </label>
                    <input
                      type="text"
                      name="delivery_address"
                      value={profileDetails?.pharmacy?.delivery_address}
                      maxLength="75"
                      onChange={handleChange}
                      disabled={!isEditMode}
                      className={cn(fieldInput, "text-neutral-600")}
                      data-testid="profile-delivery-address-input"
                    />
                  </div>

                  <div className="relative">
                    <label
                      className={cn(
                        "block",
                        isEditMode ? "text-[#00567a]" : undefined
                      )}
                      data-testid="profile-ordering-address-label"
                    >
                      Ordering Address:
                    </label>
                    <input
                      type="text"
                      name="ordering_address"
                      value={profileDetails?.pharmacy?.ordering_address}
                      maxLength="75"
                      onChange={handleChange}
                      disabled={!isEditMode}
                      className={cn(fieldInput, "text-neutral-600")}
                      data-testid="profile-ordering-address-input"
                    />
                  </div>
                </div>
              ) : null}

              <div className="grid gap-6 md:grid-cols-2">
                <div className="relative">
                  <label
                    className={cn(
                      "block",
                      isEditMode && "text-neutral-600"
                    )}
                    data-testid="profile-contact-primary-label"
                  >
                    {isPrescriber || isConnectedNurse
                      ? "Email:"
                      : "Pharmacy Contact Number:"}
                  </label>
                  <input
                    type="text"
                    name="email"
                    value={
                      isPrescriber || isConnectedNurse
                        ? profileDetails.email
                        : profileDetails.phone_number
                    }
                    disabled
                    className={cn(fieldInput, "text-neutral-600")}
                    data-testid="profile-contact-primary-input"
                  />
                </div>
                {isPharmacy || isHomecare || isOutpatient ? (
                  <div className="relative">
                    <label
                      className={cn(
                        "block",
                        isEditMode && "text-neutral-600"
                      )}
                      data-testid="profile-email-label"
                    >
                      Email
                    </label>
                    <input
                      type="text"
                      name="pharmacy_address"
                      value={profileDetails.email}
                      disabled
                      className={cn(fieldInput, "text-neutral-600")}
                      data-testid="profile-email-input"
                    />
                  </div>
                ) : (
                  <div className="hidden md:block" aria-hidden />
                )}
              </div>
            </div>

            {(isPharmacy || isHomecare || isOutpatient) && (
              <React.Fragment>
                <h2 className="mb-4 mt-5 text-lg font-semibold text-[#00567a]">
                  Wholesaler Details
                </h2>

                <div className="mb-3" data-testid="profile-wholesaler-section">
                  <div
                    className={cn(
                      "grid gap-6 md:grid-cols-2",
                      !isEditMode && "[&_label]:text-neutral-500"
                    )}
                  >
                    {(profileDetails.wholesalers_data || []).map(
                      (wholesaler) => (
                        <div
                          className="min-w-0"
                          key={wholesaler.id}
                          data-testid={`profile-wholesaler-${wholesaler.id}`}
                        >
                          <Wholesaler
                            wholesaler={wholesaler}
                            handleChangeWholeSaler={handleChangeWholeSaler}
                            formFields={validateScreenFields}
                            isSubmitting={isSubmitting}
                            isEditMode={isEditMode}
                          />
                        </div>
                      )
                    )}
                  </div>
                  {isSubmitting &&
                  !validateScreenFields.wholesalers_data?.isValid ? (
                    <p className="mt-2 text-sm font-light italic text-red-600">
                      At least one Wholesaler Detail is required
                    </p>
                  ) : invalidWholeSaler?.length ? (
                    <p className="mt-2 text-sm font-light italic text-red-600">
                      {invalidWholeSaler?.split("-")[1]}
                    </p>
                  ) : null}
                </div>
              </React.Fragment>
            )}

            {(isPharmacy || isHomecare || isOutpatient) && (
              <React.Fragment>
                <h2 className="mb-4 mt-5 text-lg font-semibold text-[#00567a]">
                  Email Management
                </h2>

                <div
                  className="flex flex-wrap items-center gap-2 text-base"
                  data-testid="profile-email-management"
                >
                  <span className="text-neutral-800">
                    {profileDetails.receive_new_paf_emails ? "On" : "Off"}{" "}
                  </span>
                  <span>
                    <ToggleSwitch
                      name="receive_new_paf_emails"
                      id="receive_new_paf_emails"
                      small
                      disabled={isEditMode ? false : true}
                      checked={profileDetails.receive_new_paf_emails}
                      onChange={onChangeCheckEmail}
                    />
                  </span>
                  <span className="text-[#00567a]">PAF emails: </span>
                  <span className="text-neutral-400">
                    {profileDetails.receive_new_paf_emails
                      ? "You will receive emails for new PAFs and for PAFs that have not been actioned."
                      : "You will not receive emails for new PAFs, only for PAFs that have not been actioned."}
                  </span>
                </div>
              </React.Fragment>
            )}
          </div>
        </React.Fragment>
      )}

      {!isLoading &&
      (isPrescriber || isPharmacy || isHomecare || isOutpatient) ? (
        <DrugRegistration
          profileDetails={profileDetails}
          getProfileData={getProfileData}
        />
      ) : null}
    </div>
  );
};
export default Profile;
